/**
 * Mobile Drawer Actions Component
 *
 * Contains quick actions, notifications, create menu,
 * and account actions for the mobile drawer footer.
 */

import Link from "next/link";
import { Button } from "../../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../dropdown-menu";
import {
  Plus,
  MessageSquare,
  Settings,
  LogOut,
  Ticket,
  CheckSquare,
  UserCheck,
  Clock,
  Calendar,
  User,
  Sparkles,
} from "lucide-react";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";

interface MobileDrawerActionsProps {
  canAccessCRM: boolean;
  isFeatureEnabled: (feature: any) => boolean;
  onClose: () => void;
  onSignOut: () => void;
  appUser?: {
    first_name?: string;
    last_name?: string;
    email?: string;
  } | null;
  getUserTypeDisplay?: () => string;
}

export function MobileDrawerActions({
  canAccessCRM,
  isFeatureEnabled,
  onClose,
  onSignOut,
  appUser,
  getUserTypeDisplay,
}: MobileDrawerActionsProps) {
  const roleCheck = useRoleCheck();
  const { openModal } = useGlobalModalStack();

  return (
    <div className="space-y-4">
      {/* Enhanced Quick Actions */}
      <div className="flex items-center justify-center space-x-2 bg-card-50 border border-gray-200 dark:border-gray-700 rounded-lg p-2">
        {/* Create Actions Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="relative z-[2] h-8 px-3 hover:bg-card hover:shadow-sm transition-all"
            >
              <Plus className="h-4 w-4 mr-2" />
              <span className="text-xs">Create</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={6}
            className="z-[10001] w-56"
          >
            <DropdownMenuLabel>Create New</DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Ticket Creation */}
            {isFeatureEnabled("canCreateTickets") && (
              <DropdownMenuItem
                className="flex items-center cursor-pointer"
                onClick={() => {
                  onClose();
                  openModal(MODAL_IDS.tickets.createQuick);
                }}
              >
                <Ticket className="mr-3 h-4 w-4 text-blue-500" />
                <div>
                  <div className="font-medium">Support Ticket</div>
                  <div className="text-xs text-gray-500">Create a new ticket</div>
                </div>
              </DropdownMenuItem>
            )}

            {/* Task Creation */}
            {isFeatureEnabled("canCreateTasks") && (
              <DropdownMenuItem
                className="flex items-center cursor-pointer"
                onClick={() => {
                  onClose();
                  openModal(MODAL_IDS.tasks.create);
                }}
              >
                <CheckSquare className="mr-3 h-4 w-4 text-blue-600" />
                <div>
                  <div className="font-medium">Task</div>
                  <div className="text-xs text-gray-500">Create new task</div>
                </div>
              </DropdownMenuItem>
            )}

            {/* Contact Creation */}
            {canAccessCRM && (
              <DropdownMenuItem
                className="flex items-center cursor-pointer"
                onClick={() => {
                  onClose();
                  openModal(MODAL_IDS.contacts.createQuick);
                }}
              >
                <UserCheck className="mr-3 h-4 w-4 text-green-500" />
                <div>
                  <div className="font-medium">Contact</div>
                  <div className="text-xs text-gray-500">Add new contact</div>
                </div>
              </DropdownMenuItem>
            )}

            {/* Time Entry */}
            {isFeatureEnabled("canCreateTimeEntries") && (
              <DropdownMenuItem
                className="flex items-center cursor-pointer"
                onClick={() => {
                  onClose();
                  openModal(MODAL_IDS.timeEntries.createQuick);
                }}
              >
                <Clock className="mr-3 h-4 w-4 text-orange-500" />
                <div>
                  <div className="font-medium">Time Entry</div>
                  <div className="text-xs text-gray-500">Log time worked</div>
                </div>
              </DropdownMenuItem>
            )}

            {/* Meeting/Event - hidden for WTC Employees */}
            {!roleCheck.isWTCEmployee && (
              <DropdownMenuItem
                className="flex items-center cursor-pointer"
                onClick={() => {
                  onClose();
                  openModal(MODAL_IDS.meetings.create);
                }}
              >
                <Calendar className="mr-3 h-4 w-4 text-indigo-500" />
                <div>
                  <div className="font-medium">Meeting</div>
                  <div className="text-xs text-gray-500">Schedule meeting</div>
                </div>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Meridian AI — ask, look up, create via chat (also in Slack as @Meridian) */}
        {canAccessCRM && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-8 px-3 hover:bg-card hover:shadow-sm transition-all"
          >
            <Link href="/chat" onClick={onClose}>
              <Sparkles className="h-4 w-4 mr-2" />
              <span className="text-xs">Meridian</span>
            </Link>
          </Button>
        )}

        {/* CRM Inbox */}
        {canAccessCRM && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="h-8 px-3 hover:bg-card hover:shadow-sm transition-all"
          >
            <Link href="/crm/inbox" onClick={onClose}>
              <MessageSquare className="h-4 w-4 mr-2" />
              <span className="text-xs">Inbox</span>
            </Link>
          </Button>
        )}
      </div>

      {/* Account row: user identity + settings + sign out */}
      <div className="flex items-center justify-between gap-2">
        {/* Compact user identity */}
        {appUser && (
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="w-7 h-7 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center shrink-0">
              <User className="h-3.5 w-3.5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-card-foreground truncate leading-tight">
                {appUser.first_name
                  ? `${appUser.first_name} ${appUser.last_name || ""}`.trim()
                  : appUser.email?.split("@")[0] || "User"}
              </p>
              {getUserTypeDisplay && (
                <p className="text-xs text-muted-foreground leading-tight">{getUserTypeDisplay()}</p>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" className="px-2 py-1.5" asChild>
            <Link href="/account" onClick={onClose}>
              <Settings className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="px-2 py-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-950/40"
            onClick={onSignOut}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
